import React from 'react';

let systemMql = null;
let systemHandler = null;

const defaultSettings = {
  theme_mode: 'light',
  accent_color: '#2b6cb0',
  font_size: 'normal',
  background_style: 'solid',
  theme_preset: null,
  mood: null,
  custom_background_url: null,
  custom_banner_url: null,
};

export const ThemeContext = React.createContext({
  settings: defaultSettings,
  setSettings: () => {},
  openModal: () => {},
  closeModal: () => {},
});

function lightenColor(hex, amount = 0.2) {
  try {
    let c = (hex || '').toString().trim();
    if (!c) return hex;
    if (c.startsWith('var(')) return c; // don't transform CSS variables
    if (c[0] === '#') {
      let r, g, b;
      if (c.length === 4) {
        r = parseInt(c[1] + c[1], 16);
        g = parseInt(c[2] + c[2], 16);
        b = parseInt(c[3] + c[3], 16);
      } else if (c.length === 7) {
        r = parseInt(c.slice(1, 3), 16);
        g = parseInt(c.slice(3, 5), 16);
        b = parseInt(c.slice(5, 7), 16);
      } else {
        return hex;
      }
      const L = (v) => Math.min(255, Math.round(v + (255 - v) * amount));
      return '#' + [L(r), L(g), L(b)].map(v => v.toString(16).padStart(2, '0')).join('');
    }
    const m = c.match(/rgba?\((\s*\d+\s*),(\s*\d+\s*),(\s*\d+\s*)(?:,(.*))?\)/i);
    if (m) {
      const r = Number(m[1]);
      const g = Number(m[2]);
      const b = Number(m[3]);
      const a = m[4] ? `,${m[4]}` : '';
      const L = (v) => Math.min(255, Math.round(v + (255 - v) * amount));
      return `rgb(${L(r)}, ${L(g)}, ${L(b)}${a})`;
    }
    return hex;
  } catch (_) {
    return hex;
  }
}

export function applyThemeToDocument(settings) {
  try {
    const root = document.documentElement;
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const mode = settings.theme_mode === 'system' ? (prefersDark ? 'dark' : 'light') : (settings.theme_mode || 'light');

    // Hint UA for better native control rendering
    try { document.documentElement.style.colorScheme = mode; } catch (_) {}

    // Presets & mood mapping - Student-friendly themes
    const presets = {
      // Original themes
      rainbow_blast: { accent: '#f59e0b', headerEnd: '#ef4444', bg: mode==='dark' ? '#1E1E2F' : '#fff7ed' },
      galaxy_night: { accent: '#8b5cf6', headerEnd: '#ec4899', bg: '#0c0a1d' },
      panda_mode: { accent: '#111827', headerEnd: '#6b7280', bg: mode==='dark' ? '#1E1E2F' : '#f8fafc' },
      beach_vibes: { accent: '#06b6d4', headerEnd: '#22c55e', bg: mode==='dark' ? '#1E1E2F' : '#ecfeff' },
      // New WordPress-style themes
      ocean_breeze: { accent: '#0ea5e9', headerEnd: '#38bdf8', bg: '#f0f9ff' },
      sunset_glow: { accent: '#f97316', headerEnd: '#ec4899', bg: '#fff7ed' },
      forest_green: { accent: '#22c55e', headerEnd: '#10b981', bg: '#f0fdf4' },
      lavender_dream: { accent: '#a855f7', headerEnd: '#8b5cf6', bg: '#faf5ff' },
      cherry_blossom: { accent: '#ec4899', headerEnd: '#f472b6', bg: '#fdf2f8' },
      sunny_day: { accent: '#eab308', headerEnd: '#f59e0b', bg: '#fefce8' },
      midnight_blue: { accent: '#6366f1', headerEnd: '#8b5cf6', bg: '#0f172a' },
      classic_light: { accent: '#3b82f6', headerEnd: '#60a5fa', bg: '#ffffff' },
      mint_fresh: { accent: '#14b8a6', headerEnd: '#2dd4bf', bg: '#f0fdfa' },
      rose_gold: { accent: '#e11d48', headerEnd: '#fb7185', bg: '#fff1f2' },
      cozy_dark: { accent: '#f59e0b', headerEnd: '#fbbf24', bg: '#1c1917' },
    };
    const moods = {
      happy: { accent: '#f59e0b' },
      chill: { accent: '#06b6d4' },
      focus: { accent: '#10b981' },
    };

    const preset = settings.theme_preset ? presets[settings.theme_preset] : null;
    const mood = settings.mood ? moods[settings.mood] : null;

    const rawAccent = (mood?.accent) || (preset?.accent) || settings.accent_color || '#2b6cb0';
    const normalizedAccent = (mode === 'dark') ? normalizeAccentForDark(rawAccent) : rawAccent;
    const adjustedAccent = mode === 'dark' ? lightenColor(normalizedAccent, 0.12) : normalizedAccent;

    root.setAttribute('data-theme-mode', mode);
    root.style.setProperty('--accent', adjustedAccent);
    root.style.setProperty('--font-size-scale', settings.font_size === 'large' ? '1.06' : '1.0');

    // Student-centric & other background styles
    const bgStyle = settings.background_style;
    if (bgStyle === 'gradient') {
      root.style.setProperty('--bg', mode === 'dark' ? '#1E1E2F' : (preset?.bg || '#f6f8fb'));
      root.style.setProperty('--container-bg', mode === 'dark' ? '#2A2A3D' : '#ffffff');
      root.style.setProperty('--header-start', adjustedAccent);
      root.style.setProperty('--header-end', preset?.headerEnd || (mode === 'dark' ? '#3A3A4F' : '#1e4e8c'));
      root.style.setProperty('--pattern-image', 'none');
    } else if (bgStyle === 'pattern') {
      const pattern = 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)';
      root.style.setProperty('--bg', mode === 'dark' ? '#202124' : '#f7fafc');
      root.style.setProperty('--container-bg', mode === 'dark' ? '#2A2A3D' : '#ffffff');
      root.style.setProperty('--header-start', adjustedAccent);
      root.style.setProperty('--header-end', preset?.headerEnd || (mode === 'dark' ? '#3A3A4F' : '#1e4e8c'));
      root.style.setProperty('--pattern-image', pattern);
    } else if (bgStyle === 'chalkboard') {
      const chalkTex = 'repeating-linear-gradient(0deg, #0f3d2e, #0f3d2e 3px, #0e3528 3px, #0e3528 6px)';
      root.style.setProperty('--bg', '#0c2f25');
      root.style.setProperty('--container-bg', '#123a2c');
      root.style.setProperty('--header-start', adjustedAccent);
      root.style.setProperty('--header-end', '#0c2f25');
      root.style.setProperty('--pattern-image', chalkTex);
    } else if (bgStyle === 'notebook') {
      const notebook = 'repeating-linear-gradient(#fafafa, #fafafa 24px, #e2e8f0 25px)';
      root.style.setProperty('--bg', '#ffffff');
      root.style.setProperty('--container-bg', '#ffffff');
      root.style.setProperty('--header-start', adjustedAccent);
      root.style.setProperty('--header-end', '#60a5fa');
      root.style.setProperty('--pattern-image', notebook);
    } else if (bgStyle === 'custom_image' && settings.custom_background_url) {
      root.style.setProperty('--bg', mode === 'dark' ? '#1E1E2F' : '#f6f8fb');
      root.style.setProperty('--container-bg', mode === 'dark' ? 'rgba(30, 30, 47, 0.85)' : 'rgba(255,255,255,0.88)');
      root.style.setProperty('--header-start', adjustedAccent);
      root.style.setProperty('--header-end', mode === 'dark' ? '#3A3A4F' : '#1e4e8c');
      root.style.setProperty('--pattern-image', `url(${settings.custom_background_url})`);
    } else {
      // solid
      root.style.setProperty('--bg', mode === 'dark' ? '#1E1E2F' : (preset?.bg || '#f6f8fb'));
      root.style.setProperty('--container-bg', mode === 'dark' ? '#2A2A3D' : '#ffffff');
      root.style.setProperty('--header-start', adjustedAccent);
      root.style.setProperty('--header-end', preset?.headerEnd || (mode === 'dark' ? '#3A3A4F' : '#1e4e8c'));
      root.style.setProperty('--pattern-image', 'none');
    }

    // text colors
    root.style.setProperty('--text', mode === 'dark' ? '#E5E5E5' : '#222');
    root.style.setProperty('--text-secondary', mode === 'dark' ? '#A0A0A0' : '#4a5568');
    root.style.setProperty('--heading', mode === 'dark' ? '#FFFFFF' : '#1a365d');
    root.style.setProperty('--link', adjustedAccent);
    root.style.setProperty('--link-hover', adjustedAccent);

    // modal background for dark mode readability
    root.style.setProperty('--modal-bg', mode === 'dark' ? 'rgba(30, 30, 47, 0.95)' : '#ffffff');

    // Manage system theme change listener
    if (settings.theme_mode === 'system' && window.matchMedia) {
      try {
        if (!systemMql) systemMql = window.matchMedia('(prefers-color-scheme: dark)');
        if (!systemHandler) {
          systemHandler = () => {
            try { applyThemeToDocument(settings); } catch {}
          };
          if (systemMql.addEventListener) {
            systemMql.addEventListener('change', systemHandler);
          } else if (systemMql.addListener) {
            systemMql.addListener(systemHandler);
          }
        }
      } catch (_) {}
    } else if (systemMql && systemHandler) {
      try {
        if (systemMql.removeEventListener) {
          systemMql.removeEventListener('change', systemHandler);
        } else if (systemMql.removeListener) {
          systemMql.removeListener(systemHandler);
        }
      } catch (_) {}
      systemHandler = null;
    }

  } catch (e) {
    // no-op
  }
}

// Normalize harsh accents (like pure yellow/orange) for dark mode harmony
function normalizeAccentForDark(hex) {
  const c = (hex || '').toLowerCase();
  const candidates = ['#f59e0b', '#ffb000', '#ffc107', '#ffd700', '#fbbf24'];
  if (c && candidates.includes(c)) {
    return '#fbbf24'; // amber-400
  }
  return hex;
}
